// extractExcelImages.js
//
// Extracts names + embedded/anchored images from an .xlsx file uploaded in the browser.
//
// WHY THIS IS NEEDED:
// Pictures inserted via Excel's "Insert > Pictures" are NOT stored as cell values.
// An .xlsx is actually a zip archive. Images live in `xl/media/`, and their position
// (which row/col they're anchored to) is described in `xl/drawings/drawingN.xml`,
// linked back to the sheet via `xl/worksheets/_rels/sheetN.xml.rels`.
// SheetJS (xlsx) alone can read cell text but not these floating images, so we unzip
// the file with JSZip and walk that relationship chain ourselves.
//
// NOTE: This covers the common case (Insert > Pictures, anchored near a cell).
// It does NOT cover Excel's newer "Place in Cell" image data type (IMAGE() /
// rich-value images), which is stored completely differently and needs separate handling.
//
// npm install xlsx jszip

import * as XLSX from 'xlsx';
import JSZip from 'jszip';

/**
 * @param {File} file - the uploaded .xlsx File object (e.g. from <input type="file">)
 * @param {Object} [options]
 * @param {string} [options.sheetName] - sheet to read; defaults to the first sheet
 * @param {string} [options.nameColumn='A'] - column letter containing the name
 * @param {number} [options.headerRow=1] - 1-indexed row number of the header; data is assumed to start on the next row
 * @returns {Promise<Array<{row: number, name: string, imageBlob: Blob|null, imageUrl: string|null}>>}
 *   imageUrl is a blob: URL you can drop straight into an <img src>.
 *   Remember to URL.revokeObjectURL(imageUrl) when you're done with it (e.g. on unmount)
 *   to avoid leaking memory.
 */
export async function extractNamesAndImages(file, options = {}) {
  const { sheetName, nameColumn = 'A', headerRow = 1 } = options;

  const arrayBuffer = await file.arrayBuffer();

  const zip = await JSZip.loadAsync(arrayBuffer);
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });

  const targetSheetName = sheetName || workbook.SheetNames[0];
  const worksheet = workbook.Sheets[targetSheetName];
  if (!worksheet) {
    throw new Error(
      `Sheet "${targetSheetName}" not found. Available sheets: ${workbook.SheetNames.join(
        ', '
      )}`
    );
  }

  const sheetIndex = workbook.SheetNames.indexOf(targetSheetName) + 1;
  const images = await getAnchoredImages(zip, sheetIndex);

  const range = XLSX.utils.decode_range(worksheet['!ref']);
  const results = [];

  // headerRow is 1-indexed; data starts on the row right after it (0-indexed = headerRow)
  for (let r = headerRow; r <= range.e.r; r++) {
    const cell = worksheet[`${nameColumn}${r + 1}`];
    const name = cell ? String(cell.v).trim() : '';
    if (!name) continue;

    const matchedImage = images.find((img) => img.row === r);

    results.push({
      row: r + 1,
      name,
      imageBlob: matchedImage ? matchedImage.blob : null,
      imageUrl: matchedImage ? URL.createObjectURL(matchedImage.blob) : null
    });
  }

  return results;
}

/**
 * Walks sheet -> drawing -> media relationship chain and returns
 * [{ row, col, blob }] for every anchored picture on that sheet.
 * row/col are 0-indexed, matching the raw drawing XML.
 */
async function getAnchoredImages(zip, sheetIndex) {
  const images = [];

  const sheetRelsFile = zip.file(`xl/worksheets/_rels/sheet${sheetIndex}.xml.rels`);
  if (!sheetRelsFile) return images;

  const sheetRelsXml = await sheetRelsFile.async('string');
  const drawingMatch = sheetRelsXml.match(
    /Target="(\.\.\/drawings\/drawing\d+\.xml)"/
  );
  if (!drawingMatch) return images;

  const drawingPath = drawingMatch[1].replace('..', 'xl');
  const drawingFile = zip.file(drawingPath);
  if (!drawingFile) return images;

  const drawingXml = await drawingFile.async('string');

  const drawingRelsPath =
    drawingPath.replace('drawings/', 'drawings/_rels/') + '.rels';
  const drawingRelsFile = zip.file(drawingRelsPath);

  const relIdToTarget = {};
  if (drawingRelsFile) {
    const relsXml = await drawingRelsFile.async('string');
    const relRegex = /<Relationship[^>]*Id="(rId\d+)"[^>]*Target="([^"]+)"/g;
    let relMatch;
    while ((relMatch = relRegex.exec(relsXml)) !== null) {
      relIdToTarget[relMatch[1]] = relMatch[2];
    }
  }

  // Each anchor describes one picture's position + a reference (rId) to its media file
  const anchorRegex = /<xdr:(twoCellAnchor|oneCellAnchor)[\s\S]*?<\/xdr:\1>/g;
  let anchorMatch;
  while ((anchorMatch = anchorRegex.exec(drawingXml)) !== null) {
    const anchorXml = anchorMatch[0];

    const rowMatch = anchorXml.match(/<xdr:from>[\s\S]*?<xdr:row>(\d+)<\/xdr:row>/);
    const colMatch = anchorXml.match(/<xdr:from>[\s\S]*?<xdr:col>(\d+)<\/xdr:col>/);
    const embedMatch = anchorXml.match(/r:embed="(rId\d+)"/);
    if (!rowMatch || !embedMatch) continue;

    const target = relIdToTarget[embedMatch[1]];
    if (!target) continue;

    const mediaFile = zip.file('xl/media/' + target.split('/').pop());
    if (!mediaFile) continue;

    images.push({
      row: parseInt(rowMatch[1], 10),
      col: colMatch ? parseInt(colMatch[1], 10) : 0,
      blob: await mediaFile.async('blob')
    });
  }

  return images;
}
